import type { Category, MenuChannelAvailability, MenuItem, MenuOrderingChannel, OrderType } from '../types';

const CHANNEL_FLAG_KEYS: Record<MenuOrderingChannel, keyof MenuChannelAvailability> = {
  online: 'availableOnline',
  qr: 'availableQr',
  dineIn: 'availableDineIn',
  takeaway: 'availableTakeaway',
  delivery: 'availableDelivery',
  mealPlan: 'availableMealPlan',
};

export const MENU_CHANNEL_LABELS: Record<MenuOrderingChannel, string> = {
  online: 'Online',
  qr: 'QR table menu',
  dineIn: 'Dine-in',
  takeaway: 'Takeaway',
  delivery: 'Delivery',
  mealPlan: 'Meal plan',
};

export const ALL_MENU_ORDERING_CHANNELS: MenuOrderingChannel[] = [
  'online',
  'qr',
  'dineIn',
  'takeaway',
  'delivery',
  'mealPlan',
];

/** Channels a dish can be sold on from the till. Meal plan is not one of them. */
const POS_TILL_CHANNELS: MenuOrderingChannel[] = ['dineIn', 'takeaway', 'delivery'];

/** Map POS order type to menu channel. Returns null when channel filtering should be skipped. */
export function orderTypeToMenuChannel(orderType: OrderType | null | undefined): MenuOrderingChannel | null {
  if (!orderType) return null;
  switch (orderType) {
    case 'dine-in':
    case 'buffet':
      return 'dineIn';
    case 'takeaway':
      return 'takeaway';
    case 'delivery':
      return 'delivery';
    default:
      return null;
  }
}

export function isChannelFlagEnabled(
  flags: MenuChannelAvailability | undefined,
  channel: MenuOrderingChannel
): boolean {
  if (!flags) return true;
  const key = CHANNEL_FLAG_KEYS[channel];
  return flags[key] !== false;
}

export function isItemGloballyAvailable(item: Pick<MenuItem, 'isAvailable'>): boolean {
  return item.isAvailable !== false;
}

export function isItemVisibleForChannel(
  item: MenuItem,
  category: Category | undefined,
  channel: MenuOrderingChannel
): boolean {
  if (!isItemGloballyAvailable(item)) return false;
  if (!isChannelFlagEnabled(category, channel)) return false;
  return isChannelFlagEnabled(item, channel);
}

export function findCategoryForItem(
  categories: Category[],
  item: Pick<MenuItem, 'categoryId'>
): Category | undefined {
  return categories.find((c) => String(c.id) === String(item.categoryId));
}

export function filterMenuItemsForChannel(
  items: MenuItem[],
  categories: Category[],
  channel: MenuOrderingChannel
): MenuItem[] {
  return items.filter((item) =>
    isItemVisibleForChannel(item, findCategoryForItem(categories, item), channel)
  );
}

/** True when the dish can be sold on at least one till order type. */
export function isItemOnTill(item: MenuItem, categories: Category[]): boolean {
  if (!isItemGloballyAvailable(item)) return false;
  const category = findCategoryForItem(categories, item);
  return POS_TILL_CHANNELS.some((channel) => isItemVisibleForChannel(item, category, channel));
}

export function filterMenuItemsForOrderType(
  items: MenuItem[],
  categories: Category[],
  orderType: OrderType | null | undefined
): MenuItem[] {
  const channel = orderTypeToMenuChannel(orderType);
  if (!channel) {
    return items.filter((item) => isItemOnTill(item, categories));
  }
  return filterMenuItemsForChannel(items, categories, channel);
}

export function filterCategoriesForChannel(
  categories: Category[],
  items: MenuItem[],
  channel: MenuOrderingChannel
): Category[] {
  return categories.filter((category) => {
    if (!isChannelFlagEnabled(category, channel)) return false;
    return items.some(
      (item) =>
        String(item.categoryId) === String(category.id) &&
        isItemVisibleForChannel(item, category, channel)
    );
  });
}

export function filterCategoriesForOrderType(
  categories: Category[],
  items: MenuItem[],
  orderType: OrderType | null | undefined
): Category[] {
  const channel = orderTypeToMenuChannel(orderType);
  if (!channel) {
    return categories.filter((category) => {
      const categoryOnTill = POS_TILL_CHANNELS.some((ch) => isChannelFlagEnabled(category, ch));
      if (!categoryOnTill) return false;
      const inCategory = items.filter((item) => String(item.categoryId) === String(category.id));
      if (inCategory.length === 0) return true;
      return inCategory.some((item) => isItemOnTill(item, categories));
    });
  }
  return filterCategoriesForChannel(categories, items, channel);
}

/** Returns channel labels that are disabled on this item (for admin badges). */
export function getDisabledChannelLabels(flags: MenuChannelAvailability | undefined): string[] {
  return ALL_MENU_ORDERING_CHANNELS.filter((ch) => !isChannelFlagEnabled(flags, ch)).map(
    (ch) => MENU_CHANNEL_LABELS[ch]
  );
}

/** True when every channel except the listed ones is disabled. */
export function isOnlyOnChannels(
  flags: MenuChannelAvailability | undefined,
  enabledChannels: MenuOrderingChannel[]
): boolean {
  return ALL_MENU_ORDERING_CHANNELS.every((ch) =>
    enabledChannels.includes(ch)
      ? isChannelFlagEnabled(flags, ch)
      : !isChannelFlagEnabled(flags, ch)
  );
}

export function defaultChannelAvailability(): Required<MenuChannelAvailability> {
  return {
    availableOnline: true,
    availableQr: true,
    availableDineIn: true,
    availableTakeaway: true,
    availableDelivery: true,
    availableMealPlan: true,
  };
}

export function readChannelAvailability(
  source: MenuChannelAvailability | undefined
): Required<MenuChannelAvailability> {
  const defaults = defaultChannelAvailability();
  if (!source) return defaults;
  return {
    availableOnline: source.availableOnline !== false,
    availableQr: source.availableQr !== false,
    availableDineIn: source.availableDineIn !== false,
    availableTakeaway: source.availableTakeaway !== false,
    availableDelivery: source.availableDelivery !== false,
    availableMealPlan: source.availableMealPlan !== false,
  };
}

/** New dishes start with nothing ticked, so they are not sold until staff choose where. */
export function emptyChannelAvailability(): Required<MenuChannelAvailability> {
  return {
    availableOnline: false,
    availableQr: false,
    availableDineIn: false,
    availableTakeaway: false,
    availableDelivery: false,
    availableMealPlan: false,
  };
}

/** True when the category hides at least one channel. */
export function categoryLimitsChannels(category: MenuChannelAvailability | undefined): boolean {
  if (!category) return false;
  const flags = readChannelAvailability(category);
  return ALL_MENU_ORDERING_CHANNELS.some((channel) => !flags[CHANNEL_FLAG_KEYS[channel]]);
}

/**
 * New item visibility. A category that is on everywhere leaves every box unchecked.
 * A category limited to some places starts with only those places checked.
 */
export function channelAvailabilityForNewItem(
  category: MenuChannelAvailability | undefined
): Required<MenuChannelAvailability> {
  if (!categoryLimitsChannels(category)) return emptyChannelAvailability();
  return readChannelAvailability(category);
}

/** Channel flags the category has turned off. Those stay off on items in the category. */
export function channelsLockedByCategory(
  category: MenuChannelAvailability | undefined
): (keyof Required<MenuChannelAvailability>)[] {
  if (!categoryLimitsChannels(category)) return [];
  const flags = readChannelAvailability(category);
  return ALL_MENU_ORDERING_CHANNELS.map((channel) => CHANNEL_FLAG_KEYS[channel]).filter(
    (key) => !flags[key]
  );
}
